import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getCanLoyaltyEnrollExistingUser from 'loyalty/selectors/getCanLoyaltyEnrollExistingUser'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled, { keyframes } from 'styled-components'
import useNavigateTo from 'hooks/useNavigateTo'

// Infinite scroll animation for mobile
const infiniteScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-50% - ${rem(16)}));
  }
`

const Container = styled.div`
  padding-block: ${rem(12)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const MobileScrollContainer = styled.div`
  /* Mobile only - hide on tablet and up */
  ${mediaQueryUp.tablet} {
    display: none;
  }
  
  overflow: hidden;
  white-space: nowrap;
  position: relative;
`

const ScrollingContent = styled.div`
  display: inline-flex;
  animation: ${infiniteScroll} 16s linear infinite;
  align-items: center;
  gap: ${rem(32)};
  
  /* Pause animation on hover for better UX */
  &:hover {
    animation-play-state: paused;
  }
`

const ScrollItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(12)};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  
  /* Add hover effect for better UX */
  &:hover {
    opacity: 0.8;
  }
`

const DesktopContainer = styled.div`
  /* Hide on mobile, show on tablet and up */
  display: none;
  
  ${mediaQueryUp.tablet} {
    display: block;
  }
`

// TODO: Update this to the correct URL
const VIEW_ALL_OFFERS_URL = `/${LOYALTY_LANDING_PAGE}`

export default function LoyaltyUpgradePromoBanner() {
  const navigateTo = useNavigateTo()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const canLoyaltyEnrollExistingUser = useAppSelector(getCanLoyaltyEnrollExistingUser)
  const isLoadingLoyaltyAccount = useAppSelector((state) => state.loyalty.account.fetching)

  const [tier] = useCurrentLoyaltyTier()

  const message = useMemo(() => {
    switch (tier) {
      case 'bronze':
        return 'Even more reasons to upgrade to Silver'
      case 'silver':
        return 'Free upgrades now available to Silver members'
      case 'gold':
        return 'Enjoy an extra upgrade for your stay every year!'
      default:
        return ''
    }
  }, [tier])

  const isEligible = useMemo(() => {
    return isLoggedIn && isLoyaltyEnabled && (tier === 'silver' || tier === 'gold' || tier === 'bronze') && !isLoadingLoyaltyAccount && !canLoyaltyEnrollExistingUser
  }, [isLoggedIn, isLoyaltyEnabled, tier, isLoadingLoyaltyAccount, canLoyaltyEnrollExistingUser])

  // Helper function to render banner content for mobile scroll
  const renderMobileBannerContent = useCallback(() => {
    if (!tier) return null

    const handleScrollItemClick = (e: React.MouseEvent) => {
      // Prevent navigation if clicking on the TextLink
      if ((e.target as HTMLElement).closest('a')) {
        return
      }
      // Navigate to the loyalty landing page using client-side routing
      navigateTo(VIEW_ALL_OFFERS_URL)
    }

    return (
      <ScrollItem onClick={handleScrollItemClick}>
        <LoyaltyProductLogo height={24} />
        <LoyaltyTierPill tier={tier} kind="primary" icon={null}>New benefits</LoyaltyTierPill>
        <BodyText variant="medium">{message}</BodyText>
        <TextLink size="medium" href={VIEW_ALL_OFFERS_URL}>View all offers</TextLink>
      </ScrollItem>
    )
  }, [tier, message, navigateTo])

  // Helper function to render desktop banner content
  const renderDesktopBannerContent = useCallback(() => {
    return (
      <LayoutContainer>
        <Group direction="horizontal" gap={12} horizontalAlign="center" verticalAlign="center" wrap="wrap">
          <LoyaltyProductLogo height={24} />
          <LoyaltyTierPill tier={tier} kind="primary" icon={null}>New benefits</LoyaltyTierPill>
          <BodyText variant="medium">{message}</BodyText>
          <TextLink size="medium" href={VIEW_ALL_OFFERS_URL}>View all offers</TextLink>
        </Group>
      </LayoutContainer>
    )
  }, [tier, message])

  if (!isEligible) return null

  return (
    <Container className={`tier-${tier}`}>
      {/* Mobile infinite scroll version */}
      <MobileScrollContainer>
        <ScrollingContent>
          {/* Duplicate content multiple times for seamless infinite scroll */}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
        </ScrollingContent>
      </MobileScrollContainer>

      {/* Desktop/tablet version */}
      <DesktopContainer>
        {renderDesktopBannerContent()}
      </DesktopContainer>
    </Container>
  )
}
